/**
 * Sentry initialisation for the Rektra React frontend.
 *
 * Imported once from `src/index.js` BEFORE the React tree mounts so Sentry
 * can hook into the global error handler. DSN comes from
 * `REACT_APP_SENTRY_DSN` — missing DSN = no-op (local dev).
 *
 * Source maps: built by `yarn build` with GENERATE_SOURCEMAP=true (default).
 * Upload them to Sentry via `sentry-cli sourcemaps upload` in CI — see README.
 */
import * as Sentry from "@sentry/react";

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Local dev or misconfigured build — stay silent, don't break the app
    return false;
  }
  const environment =
    process.env.REACT_APP_ENV ||
    (process.env.NODE_ENV === "production" ? "production" : "development");
  const release =
    process.env.REACT_APP_SENTRY_RELEASE ||
    process.env.REACT_APP_BUILD_SHA ||
    "unknown";
  const tracesRate = Number(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1
  );
  const replaysRate = Number(
    process.env.REACT_APP_SENTRY_REPLAYS_SAMPLE_RATE || 0.0
  );

  Sentry.init({
    dsn,
    environment,
    release,
    tracesSampleRate: isNaN(tracesRate) ? 0.1 : tracesRate,
    replaysSessionSampleRate: isNaN(replaysRate) ? 0.0 : replaysRate,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy-first: never record input values or user-supplied text.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Scrub obvious secrets from captured events before they leave the browser.
    beforeSend(event) {
      try {
        const scrub = (obj) => {
          if (!obj || typeof obj !== "object") return;
          for (const k of Object.keys(obj)) {
            const lk = k.toLowerCase();
            if (
              lk.includes("password") ||
              lk.includes("token") ||
              lk.includes("secret") ||
              lk.includes("authorization") ||
              lk.includes("cookie")
            ) {
              obj[k] = "[scrubbed]";
            } else if (typeof obj[k] === "object") {
              scrub(obj[k]);
            }
          }
        };
        scrub(event.request?.headers);
        scrub(event.request?.cookies);
        scrub(event.extra);
        return event;
      } catch {
        return event;
      }
    },
  });
  // eslint-disable-next-line no-console
  console.info(
    `[SENTRY] Initialized · env=${environment} release=${release} traces=${tracesRate}`
  );

  // ─── PWA / display-mode telemetry (2026-04-24) ───────────────────────
  // Surfaces iOS-PWA-standalone sessions in every Sentry event + logs a
  // one-time tag so we can filter events by "was-this-a-PWA-session".
  try {
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      (typeof window.navigator.standalone === 'boolean' && window.navigator.standalone);
    const ua = window.navigator.userAgent || '';
    const deviceTag = /iPhone|iPad|iPod/i.test(ua) ? 'ios'
                    : /Android/i.test(ua) ? 'android'
                    : 'desktop';
    Sentry.setTag('display_mode', isStandalone ? 'standalone' : 'browser');
    Sentry.setTag('device_family', deviceTag);
    Sentry.setTag('is_pwa', String(isStandalone));
    // eslint-disable-next-line no-console
    console.info('[SENTRY] PWA telemetry', {
      display_mode: isStandalone ? 'standalone' : 'browser',
      device_family: deviceTag,
      userAgent: ua.slice(0, 200),
      standaloneLegacy: window.navigator.standalone,
      matchMedia: !!window.matchMedia?.('(display-mode: standalone)')?.matches,
    });
    // Also record a breadcrumb so the context travels with every event
    Sentry.addBreadcrumb({
      category: 'session',
      level: 'info',
      message: 'pwa-telemetry',
      data: {
        display_mode: isStandalone ? 'standalone' : 'browser',
        device_family: deviceTag,
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[SENTRY] PWA telemetry failed (non-fatal)', e);
  }
  // Expose for superadmin debug probes (can be called from browser console)
  if (typeof window !== "undefined") {
    window.Sentry = Sentry;
  }
  return true;
}

export { Sentry };
